import { Candidate, CandidateCreateRequest, CandidateUpdateRequest, StageChangeRequest, Document, RecentActivity, PaginatedResult, Note } from '../../types/types';
import { apiClient } from './apiClient';

class CandidateService {
    private baseUrl = '/candidate';

    /**
     * Get recent candidates (added in the last 7 days)
     * @returns Promise with array of recent candidates
     */
    async getRecentCandidates(): Promise<Candidate[]> {
        try {
            const response = await apiClient.get(`${this.baseUrl}/new-this-week`);
            return response.data;
        } catch (error) {
            console.error('Error fetching recent candidates:', error);
            throw error;
        }
    }
    /**
     * Get all candidates
     * @returns Promise with array of candidates
     */
    async getCandidates(): Promise<PaginatedResult<Candidate>> {
        try {
            const response = await apiClient.get(this.baseUrl);
            return response.data;
        } catch (error) {
            console.error('Error fetching candidates:', error);
            throw error;
        }
    }

    /**
     * Get candidates filtered by stage
     * @param stageId Stage ID to filter by
     * @returns Promise with array of candidates in the specified stage
     */
    async getCandidatesByStage(stageId: string): Promise<Candidate[]> {
        try {
            const response = await apiClient.get(`${this.baseUrl}/stage/${stageId}`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching candidates for stage ${stageId}:`, error);
            throw error;
        }
    }

    /**
     * Get candidates filtered by job
     * @param jobId Job ID to filter by
     * @returns Promise with array of candidates for the specified job
     */
    async getCandidatesByJob(jobId: string): Promise<Candidate[]> {
        try {
            const response = await apiClient.get(`${this.baseUrl}/job/${jobId}`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching candidates for job ${jobId}:`, error);
            throw error;
        }
    }

    /**
     * Get a specific candidate by ID
     * @param id Candidate ID
     * @returns Promise with candidate data
     */
    async getCandidateById(id: string): Promise<Candidate> {
        try {
            const response = await apiClient.get(`${this.baseUrl}/${id}`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching candidate with ID ${id}:`, error);
            throw error;
        }
    }

    /**
     * Create a new candidate
     * @param candidateData Candidate data to create
     * @returns Promise with created candidate data
     */
    async createCandidate(candidateData: CandidateCreateRequest): Promise<Candidate> {
        try {
            const response = await apiClient.post(`${this.baseUrl}/create`, candidateData);
            return response.data;
        } catch (error) {
            console.error('Error creating candidate:', error);
            throw error;
        }
    }

    /**
     * Update an existing candidate
     * @param id Candidate ID
     * @param candidateData Updated candidate data
     * @returns Promise with updated candidate data
     */
    async updateCandidate(id: string, candidateData: CandidateUpdateRequest): Promise<Candidate> {
        try {
            const response = await apiClient.put(`${this.baseUrl}/${id}`, candidateData);
            return response.data;
        } catch (error) {
            console.error(`Error updating candidate with ID ${id}:`, error);
            throw error;
        }
    }

    /**
     * Delete a candidate
     * @param id Candidate ID to delete
     * @returns Promise with deletion confirmation
     */
    async deleteCandidate(id: string): Promise<void> {
        try {
            await apiClient.delete(`${this.baseUrl}/${id}`);
        } catch (error) {
            console.error(`Error deleting candidate with ID ${id}:`, error);
            throw error;
        }
    }

    /**
     * Change a candidate's stage
     * @param stageChangeData Stage change data
     * @returns Promise with updated candidate data
     */
    async changeStage(id: string, stageChangeData: StageChangeRequest): Promise<Candidate> {
        try {
            const response = await apiClient.post(`${this.baseUrl}/${id}/change-stage`, stageChangeData);
            return response.data;
        } catch (error) {
            console.error('Error changing candidate stage:', error);
            throw error;
        }
    }

    /**
     * Upload a document for a candidate
     * @param candidateId Candidate ID
     * @param file File to upload
     * @returns Promise with uploaded document data
     */
    async uploadDocument(candidateId: string, file: File): Promise<Document> {
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('candidateId', candidateId);

            const response = await apiClient.post(
                '/document',
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error(`Error uploading document for candidate ${candidateId}:`, error);
            throw error;
        }
    }

    /**
     * Get all documents for a candidate
     * @param candidateId Candidate ID
     * @returns Promise with array of documents
     */
    async getCandidateDocuments(candidateId: string): Promise<Document[]> {
        try {
            const response = await apiClient.get(`/document/candidate/${candidateId}`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching documents for candidate ${candidateId}:`, error);
            throw error;
        }
    }

    /**
     * Delete a document
     * @param candidateId Candidate ID
     * @param documentId Document ID to delete
     * @returns Promise with deletion confirmation
     */
    async deleteDocument(candidateId: string, documentId: string): Promise<void> {
        try {
            await apiClient.delete(`/document/${documentId}`);
        } catch (error) {
            console.error(`Error deleting document ${documentId}:`, error);
            throw error;
        }
    }

    /**
     * Download a document
     * @param candidateId Candidate ID
     * @param documentId Document ID to download
     * @returns Promise with document blob
     */
    async downloadDocument(candidateId: string, documentId: string): Promise<Blob> {
        try {
            const response = await apiClient.get(
                `/document/${documentId}/download`,
                { responseType: 'blob' }
            );
            return response.data;
        } catch (error) {
            console.error(`Error downloading document ${documentId}:`, error);
            throw error;
        }
    }
    /**
     * Get candidate activities (stage changes, document uploads, etc.)
     * @param candidateId Candidate ID
     * @returns Promise with array of candidate activities
     */
    async getCandidateActivities(candidateId: string): Promise<RecentActivity[]> {
        try {
            const response = await apiClient.get(`${this.baseUrl}/${candidateId}/activities`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching activities for candidate ${candidateId}:`, error);
            throw error;
        }
    }
    /**
     * Get candidate notes
     * @param candidateId Candidate ID
     * @returns Promise with array of candidate notes
     */
    async getNotes(candidateId: string): Promise<Note[]> {
        try {
            const response = await apiClient.get(`${this.baseUrl}/${candidateId}/notes`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching notes for candidate ${candidateId}:`, error);
            throw error;
        }
    };
    /**
     * Add note
     * @param candidateId
     * @param text
     * @returns
     */
    async addNote(candidateId: string, text: string): Promise<Note> {
        try {
            const response = await apiClient.post(`${this.baseUrl}/${candidateId}/notes`, { text });
            return response.data;
        } catch (error) {
            console.error(`Error fetching notes for candidate ${candidateId}:`, error);
            throw error;
        }
    };
    /**
     * Updates note
     * @param noteId
     * @param text
     */
    async updateNote(noteId: string, text: string): Promise<void> {
        try {
            await apiClient.put(`/note/${noteId}`, { text });
        } catch (error) {
            console.error(`Error updating note ${noteId}:`, error);
            throw error;
        }
    };
    /**
     * Deletes note
     * @param noteId
     */
    async deleteNote(noteId: string): Promise<void> {
        try {
            await apiClient.delete(`/note/${noteId}`);
        } catch (error) {
            console.error(`Error deleting note ${noteId}:`, error);
            throw error;
        }
    };
}

export const candidateService = new CandidateService();